import { getSpellAoeFitRequest, useSpellAoeFitRequest } from '../../lib/spell-aoe-fit-store';
import { useSrdLabel } from './srd-label';
import { useEffect, useCallback, useRef, useState, useMemo, Suspense, lazy } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCharacterHudStore } from '../../lib/character-hud-store';
import type { HudCharacterData } from '../../lib/character-hud-store';
import type { DeepPartial } from '../../lib/character-hud-store';
import type { FullSheetTab } from './types';
import { useI18n } from '../../i18n';
import { CombatTab } from './CombatTab';
import { isRoleplayModeEnabled } from '../../lib/feature-flags';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import ConfirmDialog from '../ConfirmDialog';

const CharacterMode = lazy(() => import('./character-mode/CharacterMode'));
const RoleplayMode = lazy(() => import('./roleplay-mode/RoleplayMode'));
const BuilderTab = lazy(() => import('./BuilderTab'));

/** Loading fallback while a lazy tab chunk resolves — a spinner, not a bare
 * "..." that reads as a stuck/broken state. */
const TabLoading = () => (
  <div className="flex items-center justify-center py-16" style={{ color: 'var(--color-muted-foreground)' }}>
    <Loader2 className="animate-spin" size={20} aria-label="loading" />
  </div>
);

interface FullSheetOverlayProps {
  open: boolean;
  onClose: (currentTab?: FullSheetTab) => void;
  initialTab?: FullSheetTab;
  /** Pre-adapted character data from CharacterHudOverlay (V008 fix) */
  adaptedData?: HudCharacterData | null;
  /** Rules instance from CharacterHudOverlay */
  rules?: unknown;
  /** WS send function for write operations */
  send?: (msg: any) => void;
  /** Current sheet ID for write operations */
  sheetId?: string;
  /** Subscribe to incoming WS messages (V004: used to cancel roll timeout) */
  onMessage?: (handler: (msg: any) => void) => () => void;
  /** Embedded mode: no backdrop, no fixed positioning, no Esc handler (for PopoutWindow) */
  embedded?: boolean;
  /**
   * Patch dispatcher for adapter-mode writes (Combat/Character/Roleplay sections
   * call store.patch under the hood). Without this, the adapter store silently
   * drops mutations с warning «patch() called on read-only adapter store without
   * onPatch — ignored». CharacterHudOverlay wires this к its debounced/optimistic
   * dispatcher; standalone callers (PopoutWindow в TokenManager) must wire их
   * own или accept read-only mode.
   */
  onPatch?: (diff: DeepPartial<HudCharacterData>) => void;
}

/** BF-09: Track whether builder wizard has been modified */
let builderDirtyRef = false;
export function setBuilderDirty(dirty: boolean) { builderDirtyRef = dirty; }

function FullSheetOverlayInner({ open, onClose, initialTab = 'combat', adaptedData, rules: parentRules, send, sheetId, onMessage, embedded, onPatch }: FullSheetOverlayProps) {
  const { t } = useI18n();
  const { L, Props } = useSrdLabel();
  // V008: Use adapted data from parent if available, otherwise fall back to singleton store.
  // Bug fix 2026-06-01: forward onPatch к adapter store so CombatTab edits actually dispatch
  // (без onPatch store.patch() silently drops с warning, blocking ВСЕ +/- кнопки).
  const storeOpts = adaptedData
    ? { sheetData: adaptedData, rules: parentRules, ...(onPatch ? { onPatch } : {}) }
    : undefined;
  const { data, store, helpers, rules } = useCharacterHudStore(storeOpts);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // RoleplayMode is feature-flagged off by default (see lib/feature-flags.ts).
  // If the persisted lastTab was 'roleplay' but the flag is now off, fall
  // back to 'character' so the overlay does not open into a hidden tab.
  const roleplayEnabled = useMemo(() => isRoleplayModeEnabled(), []);
  const safeInitialTab: string =
    (initialTab === 'roleplay' && !roleplayEnabled) || (initialTab === 'builder' && !send) ? 'character' : initialTab;
  const [activeTab, setActiveTab] = useState<string>(safeInitialTab);

  // BF-09: Dirty check before close — warn if builder wizard has unsaved changes
  // BF-16: Pass current tab to parent so it can persist lastTab
  const [askDiscard, setAskDiscard] = useState(false);
  const safeClose = useCallback(() => {
    if (activeTab === 'builder' && builderDirtyRef) { setAskDiscard(true); return; }
    onClose(activeTab as FullSheetTab);
  }, [onClose, activeTab]);

  // «Примерить зону» from the Бой tab: the map must be seen to fit an area on it — a fit that starts while the sheet is
  // open closes the sheet (back to the HUD). A fit already running when the sheet opens does not.
  const fitNonce = useSpellAoeFitRequest()?.nonce;
  const fitNonceAtOpen = useRef(fitNonce);
  useEffect(() => { if (open) fitNonceAtOpen.current = getSpellAoeFitRequest()?.nonce; }, [open]);
  useEffect(() => {
    if (open && fitNonce !== undefined && fitNonce !== fitNonceAtOpen.current) {
      fitNonceAtOpen.current = fitNonce;
      safeClose();
    }
  }, [open, fitNonce, safeClose]);

  // Esc handler — skip if a Radix popover is open (BI-004: breakdown closes first)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (document.querySelector('[role="alertdialog"]')) return; // the discard question answers Esc itself
      const openPopover = document.querySelector('[data-radix-popper-content-wrapper]');
      if (openPopover) return; // Let Radix handle Esc for popover first
      safeClose();
    }
  }, [safeClose]);

  useEffect(() => {
    if (!open || embedded) return; // Skip Esc handler in embedded mode (PopoutWindow handles its own close)
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, embedded, handleKeyDown]);

  if (!open) return null;

  // Combat-tab specific helpers / vars moved into CombatTab component.

  return (
    <>
      {/* Backdrop — BF-09: uses safeClose for dirty check. Hidden in embedded mode. */}
      {!embedded && (
        <div
          className="fixed inset-0 bg-shade/80"
          style={{ zIndex: 'var(--z-fullsheet)' }}
          onClick={safeClose}
          aria-label={t('characterHud.close')}
        />
      )}

      {/* Paper frame */}
      <div
        ref={scrollContainerRef}
        data-testid="full-sheet-overlay"
        className={embedded ? 'rounded-xl flex flex-col' : 'fixed rounded-xl flex flex-col'}
        data-theme="paper"
        style={embedded ? {
          width: '100%',
          height: '100%',
          backgroundColor: 'var(--color-background)',
          overflowY: 'auto',
        } : {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(1200px, 92vw)',
          height: 'min(800px, 90vh)',
          zIndex: 'var(--z-fullsheet)',
          backgroundColor: 'var(--color-background)',
          border: '1px solid var(--color-border)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          overflow: 'auto',
        }}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-4 pb-4">
            <Button type="button" variant="text" size="inline" className="text-body-s font-medium" onClick={safeClose}>
              {t('characterHud.backToHud')}
            </Button>
            <span className="text-body-s text-muted-foreground">
              {data.identity.name} · {L(data.identity.className)} {data.identity.level}
            </span>
          </div>

          {/* Tabs — underline style (paper-tab class) */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList
              style={{
                display: 'flex',
                width: '100%',
                background: 'var(--paper-bg-2)',
                borderRadius: 0,
                borderBottom: '1px solid var(--paper-rule)',
                padding: 0,
                height: 36,
              }}
            >
              <TabsTrigger
                value="combat"
                className="paper-tab"
                style={{ flex: 1, borderRadius: 0, background: 'transparent', borderRight: '1px solid var(--paper-rule)' }}
              >
                {t('characterHud.tabCombat')}
              </TabsTrigger>
              <TabsTrigger
                value="character"
                className="paper-tab"
                style={{ flex: 1, borderRadius: 0, background: 'transparent', borderRight: '1px solid var(--paper-rule)' }}
              >
                {t('characterHud.tabCharacter')}
              </TabsTrigger>
              {roleplayEnabled && (
                <TabsTrigger
                  value="roleplay"
                  className="paper-tab"
                  style={{ flex: 1, borderRadius: 0, background: 'transparent', borderRight: '1px solid var(--paper-rule)' }}
                >
                  {t('characterHud.tabRoleplay')}
                </TabsTrigger>
              )}
              {/* GN (Н7): a read-only view (another player's sheet) has nothing to rebuild */}
              {send && (
                <TabsTrigger
                  value="builder"
                  className="paper-tab"
                  style={{ flex: 1, borderRadius: 0, background: 'transparent' }}
                >
                  {t('characterHud.tabBuilder')}
                </TabsTrigger>
              )}
            </TabsList>

            {/* Combat tab — full prototype layout (see CombatTab.tsx) */}
            <TabsContent value="combat" className="mt-4">
              <CombatTab data={data} store={store} helpers={helpers} send={send} sheetId={sheetId} onMessage={onMessage} />
            </TabsContent>

            {/* Character tab — full character mode */}
            <TabsContent value="character" className="mt-4">
              <Suspense fallback={<TabLoading />}>
                <CharacterMode
                  scrollContainerRef={scrollContainerRef}
                  send={send}
                  sheetId={sheetId}
                  data={data}
                  store={store}
                  helpers={helpers}
                  onMessage={onMessage}
                />
              </Suspense>
            </TabsContent>

            {/* Roleplay tab — feature-flagged (see lib/feature-flags.ts).
                Writes persist via store.patch → write-adapter sheet:rp-*
                commands (B-01); echoes mirror back through
                useCharacterSheets.handleRp*. */}
            {roleplayEnabled && (
              <TabsContent value="roleplay" className="mt-4">
                <Suspense fallback={<TabLoading />}>
                  <RoleplayMode scrollContainerRef={scrollContainerRef} data={data} store={store} />
                </Suspense>
              </TabsContent>
            )}

            {/* «Создание» — the one character wizard, rebuilding this sheet */}
            <TabsContent value="builder" className="mt-4">
              <Suspense fallback={<TabLoading />}>
                <BuilderTab
                  send={send}
                  sheetId={sheetId}
                  initialName={adaptedData?.identity?.name ?? ''}
                  hasCharacter={!!(data.identity.className || data.identity.race || data.identity.background || data.classData.length > 0
                    || data.inventory.length > 0 || data.features.length > 0 || data.spells.known.length > 0 || (data.xp ?? 0) > 0
                    || data.rp.backstory || data.rp.goals.length > 0 || data.rp.npcs.length > 0 || data.combat.notes)}
                  onDone={() => setActiveTab('character')}
                />
              </Suspense>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <ConfirmDialog
        open={askDiscard}
        onOpenChange={setAskDiscard}
        title={t('characterHud.builder.confirmDiscard')}
        description=""
        onConfirm={() => { builderDirtyRef = false; onClose(activeTab as FullSheetTab); }}
      />
    </>
  );
}

/** GN: the sheet uses the UI vocabulary's own sizes (ui/legacy.tsx is gone); its paper look comes from the token values
 *  of [data-theme="paper"] (styles/paper-theme.css), not from a second set of primitives. */
export function FullSheetOverlay(props: React.ComponentProps<typeof FullSheetOverlayInner>): React.JSX.Element {
  return <FullSheetOverlayInner {...props} />;
}
